import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import { actions } from "app/configSlice";
import I18n from "common/i18n";
import { CloseBtn, PlanBadge } from "common/elements";
import { useScale, usePlaySound, shouldApplyGuestRestrictions } from "common/utils";
import { usePlan } from "features/subscription";
import { AccountType } from "./types";
import { UserStock } from "./UserStock";
import { UserStatus } from "./UserStatus";
import { UserProfile } from "./UserProfile";
import { UserMessage } from "./userMessage";
import { UserSetting } from "./UserSetting";
import { UserAccount } from "./UserAccount";
import { selectAllChatRoomMessageSync } from "./slice";
import { UserProgressCourse } from "./UserProgressCourse";
import { UserProgressCreator } from "./UserProgressCreator";
import {
  IconMail,
  IconItem,
  IconSmile,
  IconSetting,
  IconProgress,
  IconUserProfile,
} from "./assets";

export enum UserProfileNavigation {
  PROGRESS_COURSE = "PROGRESS_COURSE",
  PROGRESS_CREATOR = "PROGRESS_CREATOR",
  PROFILE = "profile",
  STOCK = "stock",
  SETTING = "setting",
  MESSAGE = "message",
}

const SakidoriBadge = () => (
  <div className="flex-row-el flex-center rounded-full p-2" style={{ backgroundColor: "#9575CD" }}>
    <div className="flex-row-el flex-center">
      <div className="flex-col-center w-[24px] h-[24px] rounded-full mr-1 bg-white">
        <span className="material-symbols-outlined !text-base" style={{ color: "#9575CD" }}>
          workspace_premium
        </span>
      </div>
      <p className="text-body text-xl text-white">{I18n.t("MSG_PLAN_SAKIDORI")}</p>
    </div>
  </div>
);

export const UserPage = () => {
  const play = usePlaySound();
  const plan = usePlan();
  const { scale } = useScale();
  const dispatch = useDispatch();
  const [navigation, setNavigation] = useState(UserProfileNavigation.PROGRESS_COURSE);
  const userProfile = useSelector((state: RootState) => state.config.dialog.user_profile);
  const active = useSelector((state: RootState) => state.user.active);
  const parent = useSelector((state: RootState) => state.user.parent);
  const chatRoomMessageSync = useSelector(selectAllChatRoomMessageSync);

  const handleClose = () => {
    play();
    dispatch(
      actions.updateDialog({
        type: DialogType.USER_PROFILE,
        value: false,
      }),
    );
  };

  const hasUnreadMessage =
    chatRoomMessageSync.filter(
      (sync) =>
        sync.uid === active?.uid &&
        (dayjs(sync.latestMessageCreatedAt, "YYYY-MM-DDTHH:mm:ssZ").diff(
          dayjs(sync.latestReadAt, "YYYY-MM-DDTHH:mm:ssZ"),
        ) > 0 ||
          (sync.latestReadAt === null && sync.latestMessageCreatedAt !== null)),
    ).length > 0;

  const handleNavigate = (navigation: UserProfileNavigation) => () => {
    play();
    setNavigation(navigation);
  };

  useEffect(() => {
    setNavigation(UserProfileNavigation.PROGRESS_COURSE);
  }, [active?.uid]);

  return (
    <Dialog
      fullScreen
      maxWidth={false}
      sx={{ zIndex: 10103 }}
      open={userProfile.value}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center !absolute inset-0 bg-textcolor-black/50 w-full h-full">
        <div
          style={{ transform: `scale(${scale})`, marginTop: 32 * scale }}
          className="flex-col-view items-center justify-start w-[1180px] h-[890px] rounded-lg border-[8px] border-solid border-white bg-white"
        >
          <CloseBtn close={handleClose} />
          <div className="flex-col-el w-full h-full relative rounded-[4px] bg-white overflow-hidden">
            <div
              style={{ boxShadow: "0px 12px 0px #FFD1A1" }}
              className="flex-row-center w-full h-[220px] bg-beige-60 pr-[100px]"
            >
              <UserAccount />
              <div className="flex-col-center mx-8 mb-4 cursor-pointer">
                <div
                  style={{
                    boxShadow:
                      "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                  }}
                  className="flex-col-center bg-white rounded-full mb-2 w-[160px] h-[160px] relative"
                  onClick={handleNavigate(UserProfileNavigation.PROFILE)}
                >
                  <div className="absolute top-0 -left-10 z-10">
                    {parent?.account_type === AccountType.GUEST &&
                    parent?.created_at &&
                    !shouldApplyGuestRestrictions(parent.created_at) ? (
                      <SakidoriBadge />
                    ) : (
                      <PlanBadge plan={plan} />
                    )}
                  </div>
                  <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden">
                    <img
                      key={active?.icon}
                      alt="プレイヤープロフィール画像"
                      src={active?.icon}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>

                <div
                  className="flex-col-center w-[250px] h-[40px] bg-beige-10 rounded-[32px] text-textcolor-black mt-[-20px]"
                  onClick={handleNavigate(UserProfileNavigation.PROFILE)}
                  style={{
                    boxShadow:
                      "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                  }}
                >
                  <p className="text">{active?.display_name}</p>
                </div>
              </div>

              <UserStatus />
            </div>

            <div className="flex-col-el relative w-full items-center shrink-0 flex-1">
              <div className="flex-row-el absolute -top-4 z-50">
                <div
                  className="flex-row-view bg-gray2-10 border-solid border-white rounded-xl border-4 mb-2 overflow-hidden"
                  style={{ boxShadow: "1px 1px 2px rgba(63, 74, 97, 0.4)" }}
                >
                  <div className="flex-row-el flex-center bg-beige-alt3 h-[72px] p-1 gap-1">
                    <div className="flex-row-el mx-1">
                      <p className="text-body text-textcolor-black text-[16px] leading-6 text-vertical">
                        {I18n.t("MSG_USER_PROFILE_PROGRESS")}
                      </p>
                    </div>

                    <div className="flex-row-el flex-center h-full bg-gray2-10 rounded-lg overflow-hidden">
                      <button
                        className={`opacity-button w-[168px] h-full !rounded-none text-textcolor-black ${
                          navigation === UserProfileNavigation.PROGRESS_COURSE
                            ? "!bg-green-alt"
                            : "!bg-gray2-10"
                        }`}
                        onClick={handleNavigate(UserProfileNavigation.PROGRESS_COURSE)}
                      >
                        <div className="flex-row-center">
                          <div className="flex-col-center mr-2">
                            <IconProgress />
                          </div>
                          <p className="text-body text-textcolor-black text-[20px] whitespace-pre leading-6">
                            {I18n.t("MSG_USER_PROFILE_PROGRESS_COURSE")}
                          </p>
                        </div>
                      </button>
                      <button
                        className={`opacity-button w-[168px] h-full !rounded-none text-textcolor-black ${
                          navigation === UserProfileNavigation.PROGRESS_CREATOR
                            ? "!bg-green-alt"
                            : "!bg-gray2-10"
                        }`}
                        onClick={handleNavigate(UserProfileNavigation.PROGRESS_CREATOR)}
                      >
                        <div className="flex-row-center">
                          <div className="flex-col-center mr-2">
                            <IconSmile />
                          </div>
                          <p className="text-body text-textcolor-black text-[20px] whitespace-pre leading-6">
                            {I18n.t("MSG_USER_PROFILE_PROGRESS_CREATOR")}
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>

                  <button
                    className={`opacity-button w-[168px] h-[72px] !rounded-none text-textcolor-black ${
                      navigation === UserProfileNavigation.PROFILE && "!bg-green-alt"
                    }`}
                    onClick={handleNavigate(UserProfileNavigation.PROFILE)}
                  >
                    <div className="flex-row-center">
                      <div className="flex-col-center">
                        <IconUserProfile />
                      </div>
                      <p className="text">{I18n.t("MSG_USER_PROFILE_INFO")}</p>
                    </div>
                  </button>

                  <button
                    className={`opacity-button w-[168px] h-[72px] !rounded-none text-textcolor-black ${
                      navigation === UserProfileNavigation.STOCK && "!bg-green-alt"
                    }`}
                    onClick={handleNavigate(UserProfileNavigation.STOCK)}
                  >
                    <div className="flex-row-center">
                      <div className="flex-col-center mr-[4px]">
                        <IconItem />
                      </div>
                      <p className="text">{I18n.t("MSG_USER_PROFILE_STOCK")}</p>
                    </div>
                  </button>
                  <button
                    className={`opacity-button w-[168px] h-[72px] !rounded-none text-textcolor-black ${
                      navigation === UserProfileNavigation.SETTING && "!bg-green-alt"
                    }`}
                    onClick={handleNavigate(UserProfileNavigation.SETTING)}
                  >
                    <div className="flex-row-center">
                      <div className="flex-col-center mr-2">
                        <IconSetting />
                      </div>
                      <p className="text">{I18n.t("MSG_USER_PROFILE_SETTING")}</p>
                    </div>
                  </button>
                  <button
                    className={`opacity-button w-[168px] h-[72px] !rounded-tl-none !rounded-bl-none text-textcolor-black ${
                      navigation === UserProfileNavigation.MESSAGE && "!bg-green-alt"
                    }`}
                    onClick={handleNavigate(UserProfileNavigation.MESSAGE)}
                  >
                    <div className="flex-row-center">
                      <div className="flex-col-center mr-2">
                        {hasUnreadMessage && (
                          <div className="flex-col-center !absolute right-[-5px] -top-0.5 bg-danger rounded-full w-[20px] h-[20px] z-50" />
                        )}
                        <IconMail />
                      </div>
                      <p className="text">{I18n.t("MSG_USER_PROFILE_MESSAGE")}</p>
                    </div>
                  </button>
                </div>
              </div>

              <UserProgressCourse selected={navigation === UserProfileNavigation.PROGRESS_COURSE} />
              <UserProgressCreator
                selected={navigation === UserProfileNavigation.PROGRESS_CREATOR}
                close={handleClose}
              />

              <div
                className="flex-col-view w-[1128px] h-[560px] mt-[72px] bg-gray2-10 !box-content border-solid border-4 border-white rounded-xl overflow-hidden"
                style={{
                  boxShadow: "1px 1px 4px rgba(63, 74, 97, 0.4)",
                  display:
                    navigation === UserProfileNavigation.PROGRESS_COURSE ||
                    navigation === UserProfileNavigation.PROGRESS_CREATOR
                      ? "none"
                      : "flex",
                }}
              >
                <UserProfile selected={navigation === UserProfileNavigation.PROFILE} />
                <UserStock selected={navigation === UserProfileNavigation.STOCK} />
                <UserSetting selected={navigation === UserProfileNavigation.SETTING} />
                <UserMessage selected={navigation === UserProfileNavigation.MESSAGE} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
